import { useStyles } from "../../../theme/appStyles";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  ImageList,
  ImageListItem,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import {
  useCreateMainItemMutation,
  useGetAllMainCategoryQuery,
  useGetLastProductIdQuery,
  useCheckMainItemNameQuery,
  useGetSubCategoryByArrayQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { ICategory } from "../../../Api/Interface/api.interface";
import Switch, { SwitchProps } from "@mui/material/Switch";
import CustomSwitch from "../../../Components/Switch/CustomSwitch";
import AppsIcon from "@mui/icons-material/Apps";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import { uploadImage } from "../../../Components/UploadFile/upload";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNavigate } from "react-router-dom";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import useIsMobile from "../../../Core/hook/useIsMobile";

// Image imports
const img1 = require("../../../Images/img1.jpg");
const img2 = require("../../../Images/img2.jpg");
const img3 = require("../../../Images/img3.jpg");

const Index = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobileRes = useIsMobile();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [newProduct, { isLoading }] = useCreateMainItemMutation();
  const { showErrorMessage, showMessage } = useNotifier();
  const { data: categoryData } = useGetAllMainCategoryQuery({
    departmentId: "",
    coursingId: "",
    roleId: null,
    taxeId: "",
    createdDateStart: null,
    createdDateEnd: null,
    searchText: "",
    numberOfLength: 0,
    isActive: true,
    categoryIsLooping: null,
    createdDate: null,
  });

  const { data: checkMainItemNameData, isLoading: checkMainItemNameIsLoading } =
    useCheckMainItemNameQuery({
      mainItemName: currentSearchQuery,
    });

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null
  );
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState<
    number | null
  >(null);

  const [selectedSubToSubCategoryId, setSelectedSubToSubCategoryId] = useState<
    number | null
  >(null);

  const [selectedSubToSubToSubCategoryId, setSelectedSubToSubToSubCategoryId] =
    useState<number | null>(null);

  const [showColorPicker, setShowColorPicker] = useState(false);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const { data: newLastProductIdData, isLoading: lastProductIdIsLoading } =
    useGetLastProductIdQuery();

  const [openGallery, setOpenGallery] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | undefined>(
    undefined
  );
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [productDetails, setProductDetails] = useState({
    id: "",
    isLooping: false,
    loopingTableId: "",
    variantName: "",
  });

  const { data: subCategoryData, refetch: refetchSubCategories } =
    useGetSubCategoryByArrayQuery({
      parent_ids: selectedCategoryId ? [Number(selectedCategoryId)] : [],
    });

  const { data: subCategory2Data, refetch: refetchS2bCategories } =
    useGetSubCategoryByArrayQuery({
      parent_ids: selectedSubCategoryId
        ? [Number(selectedCategoryId), Number(selectedSubCategoryId)]
        : [],
    });

  const { data: subCategory3Data, refetch: refetchS3bCategories } =
    useGetSubCategoryByArrayQuery({
      parent_ids: selectedSubCategoryId
        ? [
            Number(selectedCategoryId),
            Number(selectedSubCategoryId),
            Number(selectedSubToSubCategoryId),
          ]
        : [],
    });

  const categoryIdForItem = selectedSubToSubToSubCategoryId
    ? Number(selectedSubToSubToSubCategoryId)
    : selectedSubToSubCategoryId
    ? Number(selectedSubToSubCategoryId)
    : selectedSubCategoryId
    ? Number(selectedSubCategoryId)
    : selectedCategoryId
    ? Number(selectedCategoryId)
    : String("");

  const categoryList = useMemo(() => {
    return categoryData?.data as ICategory[];
  }, [categoryData?.data]);

  const subCategoryList = useMemo(() => {
    return subCategoryData?.data as ICategory[];
  }, [subCategoryData?.data]);

  const subToSubCategoryList = useMemo(() => {
    return subCategory2Data?.data as ICategory[];
  }, [subCategory2Data?.data]);

  const subToSubToSubCategoryList = useMemo(() => {
    return subCategory3Data?.data as ICategory[];
  }, [subCategory3Data?.data]);

  useEffect(() => {
    if (debouncedSearchQuery.length >= 1 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
    }
  }, [debouncedSearchQuery]);

  const formik = useFormik({
    initialValues: {
      mainItemName: "",
      categoryId: "",
      mainCatId: "",
      mainItemIsLooping: false,
      mainItemLoopingConstant: "",
      mainItemShortDescription: "",
      mainItemLongDescription: "",
      isActive: true,
      mainItemImg: "",
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const categoryId = selectedSubToSubToSubCategoryId
          ? Number(selectedSubToSubToSubCategoryId)
          : selectedSubToSubCategoryId
          ? Number(selectedSubToSubCategoryId)
          : selectedSubCategoryId
          ? Number(selectedSubCategoryId)
          : selectedCategoryId
          ? Number(selectedCategoryId)
          : String(values.mainCatId);

        const formData = new FormData();
        let mainItemImg: string | undefined = "";

        if (selectedImage) {
          setIsImageUploading(true);
          formData.append("mainItemImg", selectedImage);
          mainItemImg = (await uploadImage(selectedImage)) || "";
          setIsImageUploading(false);
        }

        const temData = {
          mainItemName: toProperCase(values.mainItemName),
          categoryId: categoryId,
          mainItemIsLooping: values.mainItemIsLooping,
          mainItemLoopingConstant: values.mainItemLoopingConstant,
          mainItemShortDescription: values.mainItemShortDescription,
          mainItemLongDescription: values.mainItemLongDescription,
          isActive: values.isActive,
          mainItemImg,
        };

        const addProductResponse = await newProduct(temData).unwrap();
        if (!addProductResponse.status) {
          showErrorMessage(addProductResponse.message);
        } else {
          showMessage(addProductResponse.message);
          navigate(`/main/item/view/${addProductResponse.data.id}`);
          resetForm();
          setSelectedImage(undefined);
          setOpenGallery(false);
          setSelectedCategoryId(null);
          setSelectedSubCategoryId(null);
          setSelectedSubToSubCategoryId(null);
          setSelectedSubToSubToSubCategoryId(null);
          // setProductDetails({
          //   id: addProductResponse.data.id,
          //   isLooping:
          //     addProductResponse.data.mainItemIsLooping,
          //   loopingTableId:
          //     addProductResponse.data.mainItemLoopingConstant,
          //     variantName: values.mainItemName,
          // });
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  useEffect(() => {
    formik.setFieldValue("mainItemName", currentSearchQuery);
  }, [
    checkMainItemNameData?.status,
    checkMainItemNameData?.name,
    currentSearchQuery,
  ]);

  // const formValid = useMemo(() => {
  //   return formik.values.mainItemName === "" ||
  //     formik.values.mainItemName === undefined
  //     ? false
  //     : true;
  // }, [formik]);

  const formValid = useMemo(() => {
    return (
      formik.values.mainItemName.trim() !== "" &&
      currentSearchQuery.trim() !== ""
    );
  }, [formik.values.mainItemName, currentSearchQuery]);

  const resizeImage = (
    file: File,
    maxWidth: number,
    maxHeight: number
  ): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
              });
              resolve(resizedFile);
            } else {
              reject(new Error("Canvas is empty"));
            }
          }, file.type);
        };

        img.onerror = (error) => reject(error);
      };
    });
  };

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;

    if (files && files.length > 0) {
      const file = files[0];
      const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
      const maxSize = 2048 * 1024;

      if (!validTypes.includes(file.type)) {
        showErrorMessage("The file must be an image (jpeg, png, jpg, gif).");
        setSelectedImage(undefined);
      } else if (file.size > maxSize) {
        showErrorMessage("The file must be smaller than 2 MB.");
        setSelectedImage(undefined);
      } else {
        try {
          const resizedImage = await resizeImage(file, 500, 500);
          setSelectedImage(resizedImage);
        } catch (error) {
          showErrorMessage("Failed to resize the image.");
          setSelectedImage(undefined);
        }
      }
    } else {
      setSelectedImage(undefined);
    }
  };

  const handleGallerySelection = (selectedImage: string) => {
    fetch(selectedImage)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], "image.jpg", { type: blob.type });
        setSelectedImage(file);
      });
    setOpenGallery(false);
  };

  const openGalleryDialog = () => {
    setOpenGallery(true);
  };

  const closeGalleryDialog = () => {
    setOpenGallery(false);
  };

  const galleryImages = [img2, img3, img2, img3, img2, img3];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(event.target as Node)
      ) {
        setShowColorPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleMainCategoryChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    const categoryId = event.target.value as number;
    setSelectedCategoryId(categoryId);
    setSelectedSubCategoryId(null);
    setSelectedSubToSubCategoryId(null);
    setSelectedSubToSubToSubCategoryId(null);
    formik.setFieldValue("mainCatId", categoryId);

    const selectedCategory = categoryList.find(
      (category) => category.id == categoryId
    );

    if (selectedCategory) {
      formik.setFieldValue(
        "mainItemIsLooping",
        selectedCategory.categoryIsLooping
      );
      formik.setFieldValue(
        "mainItemLoopingConstant",
        selectedCategory.categoryLoopingConstant
      );
    }
  };

  const handleSubCategoryChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    const subCategoryId = event.target.value as number;
    setSelectedSubCategoryId(subCategoryId);
    setSelectedSubToSubCategoryId(null);
    setSelectedSubToSubToSubCategoryId(null);

    const selectedCategory = subCategoryList.find(
      (category) => category.id == subCategoryId
    );

    if (selectedCategory) {
      formik.setFieldValue(
        "mainItemIsLooping",
        selectedCategory.categoryIsLooping
      );
      formik.setFieldValue(
        "mainItemLoopingConstant",
        selectedCategory.categoryLoopingConstant
      );
    }
  };

  const handleSubToSubCategoryChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    const selectedSubToSubCategoryId = event.target.value as number;
    setSelectedSubToSubCategoryId(selectedSubToSubCategoryId);
    setSelectedSubToSubToSubCategoryId(null);

    const selectedCategory = subToSubCategoryList.find(
      (category) => category.id == selectedSubToSubCategoryId
    );

    if (selectedCategory) {
      formik.setFieldValue(
        "mainItemIsLooping",
        selectedCategory.categoryIsLooping
      );
      formik.setFieldValue(
        "mainItemLoopingConstant",
        selectedCategory.categoryLoopingConstant
      );
    }
  };

  const handleSubToSubToSubCategoryChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    const selectedSubToSubToSubCategoryId = event.target.value as number;
    setSelectedSubToSubToSubCategoryId(selectedSubToSubToSubCategoryId);

    const selectedCategory = subToSubToSubCategoryList.find(
      (category) => category.id == selectedSubToSubToSubCategoryId
    );

    if (selectedCategory) {
      formik.setFieldValue("isLooping", selectedCategory.categoryIsLooping);
      formik.setFieldValue(
        "mainItemLoopingConstant",
        selectedCategory.categoryLoopingConstant
      );
    }
  };

  if (lastProductIdIsLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="10vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  const cancelFunction = () => {
    formik.resetForm();
    setSelectedImage(undefined);
    setOpenGallery(false);
    setSelectedCategoryId(null);
    setSelectedSubCategoryId(null);
    setSelectedSubToSubCategoryId(null);
    setSelectedSubToSubToSubCategoryId(null);
  };

  return (
    <div>
      <Grid container spacing={3}>
        <Grid item lg={3} md={12} sm={12} xs={12}>
          <Card className={classes.stepperCard}>
            <Grid
              container
              justifyContent={"space-between"}
              spacing={5}
              sx={{ borderBottom: 2, padding: 2 }}
              className={classes.titleGrid}
            >
              <Grid
                item
                lg={12}
                md={12}
                sm={12}
                xs={12}
                pt={2}
                alignContent={"center"}
              >
                <Typography className={classes.cardTitle}>Create</Typography>
              </Grid>
            </Grid>
          </Card>
        </Grid>
        <Grid item lg={9} md={12} sm={12} xs={12}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Card className={classes.card}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 2, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      pt={2}
                      alignContent={"center"}
                    >
                      <Typography className={classes.cardTitle}>
                        Add Product Image
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} p={"24px"}>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Box className={classes.imgBox}>
                        {selectedImage && (
                          <img
                            src={URL.createObjectURL(selectedImage)}
                            alt="Uploaded"
                            className={classes.SelectImageBox}
                          />
                        )}
                        <div>
                          <input
                            accept="image/*"
                            style={{ display: "none" }}
                            id="icon-button-file-1"
                            type="file"
                            onChange={handleImageChange}
                          />
                          <div
                            style={{
                              textAlign: "center",
                            }}
                          >
                            <label htmlFor="icon-button-file-1">
                              <IconButton
                                color="primary"
                                aria-label="upload picture"
                                component="span"
                              >
                                <CloudUploadIcon sx={{ fontSize: 45 }} />
                              </IconButton>
                            </label>
                            <IconButton
                              color="primary"
                              aria-label="select from gallery"
                              component="span"
                              onClick={openGalleryDialog}
                            >
                              <AppsIcon sx={{ fontSize: 45 }} />
                            </IconButton>
                            <div>PNG, JPG and JPEG are allowed</div>
                          </div>
                        </div>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                <Card className={classes.card}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 2, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      pt={2}
                      alignContent={"center"}
                    >
                      <Typography className={classes.cardTitle}>
                        Product Category
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} p={"24px"}>
                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Main Category
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          display="flex"
                          alignItems="center"
                        >
                          <TextField
                            select
                            fullWidth
                            size="small"
                            className={classes.textField}
                            value={selectedCategoryId || ""}
                            onChange={handleMainCategoryChange}
                            InputLabelProps={{ shrink: true }}
                          >
                            <MenuItem
                              value=""
                              disabled
                              style={{ color: "gray" }}
                            >
                              Select an option
                            </MenuItem>
                            {categoryList &&
                              categoryList.map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                  {category.categoryName}
                                </MenuItem>
                              ))}
                          </TextField>
                        </Grid>
                      </Grid>
                    </Grid>

                    {selectedCategoryId && subCategoryList.length > 0 && (
                      <Grid item lg={6} md={6} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Sub Category
                            </Typography>
                          </Grid>
                          <Grid
                            item
                            lg={12}
                            md={12}
                            sm={12}
                            xs={12}
                            display="flex"
                            alignItems="center"
                          >
                            <TextField
                              select
                              fullWidth
                              size="small"
                              className={classes.textField}
                              value={
                                selectedSubCategoryId === null
                                  ? ""
                                  : selectedSubCategoryId
                              }
                              onChange={handleSubCategoryChange}
                              InputLabelProps={{ shrink: true }}
                            >
                              <MenuItem
                                value=""
                                disabled
                                style={{ color: "gray" }}
                              >
                                Select an option
                              </MenuItem>
                              {subCategoryList.map((subcategory) => (
                                <MenuItem
                                  key={subcategory.id}
                                  value={subcategory.id}
                                >
                                  {subcategory.categoryName}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        </Grid>
                      </Grid>
                    )}

                    {selectedCategoryId &&
                      selectedSubCategoryId &&
                      subToSubCategoryList.length > 0 && (
                        <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Sub-to-Sub Category
                              </Typography>
                            </Grid>
                            <Grid
                              item
                              lg={12}
                              md={12}
                              sm={12}
                              xs={12}
                              display="flex"
                              alignItems="center"
                            >
                              <TextField
                                select
                                fullWidth
                                size="small"
                                className={classes.textField}
                                value={
                                  selectedSubToSubCategoryId === null
                                    ? ""
                                    : selectedSubToSubCategoryId
                                }
                                onChange={handleSubToSubCategoryChange}
                                InputLabelProps={{ shrink: true }}
                              >
                                <MenuItem
                                  value=""
                                  disabled
                                  style={{ color: "gray" }}
                                >
                                  Select an option
                                </MenuItem>
                                {subToSubCategoryList.map(
                                  (subToSubcategory) => (
                                    <MenuItem
                                      key={subToSubcategory.id}
                                      value={subToSubcategory.id}
                                    >
                                      {subToSubcategory.categoryName}
                                    </MenuItem>
                                  )
                                )}
                              </TextField>
                            </Grid>
                          </Grid>
                        </Grid>
                      )}

                    {selectedCategoryId &&
                      selectedSubCategoryId &&
                      selectedSubToSubCategoryId &&
                      subToSubToSubCategoryList.length > 0 && (
                        <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Sub-to-Sub-to-Sub Category
                              </Typography>
                            </Grid>
                            <Grid
                              item
                              lg={12}
                              md={12}
                              sm={12}
                              xs={12}
                              display="flex"
                              alignItems="center"
                            >
                              <TextField
                                select
                                fullWidth
                                size="small"
                                className={classes.textField}
                                value={
                                  selectedSubToSubToSubCategoryId === null
                                    ? ""
                                    : selectedSubToSubToSubCategoryId
                                }
                                onChange={handleSubToSubToSubCategoryChange}
                                InputLabelProps={{ shrink: true }}
                              >
                                <MenuItem
                                  value=""
                                  disabled
                                  style={{ color: "gray" }}
                                >
                                  Select an option
                                </MenuItem>
                                {subToSubToSubCategoryList.map(
                                  (subToSubToSubcategory) => (
                                    <MenuItem
                                      key={subToSubToSubcategory.id}
                                      value={subToSubToSubcategory.id}
                                    >
                                      {subToSubToSubcategory.categoryName}
                                    </MenuItem>
                                  )
                                )}
                              </TextField>
                            </Grid>
                          </Grid>
                        </Grid>
                      )}
                  </Grid>
                </Card>
              </Grid>

              <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                <Card className={classes.card}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 2, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      pt={2}
                      alignContent={"center"}
                    >
                      <Typography className={classes.cardTitle}>
                        Product Information
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} p={"24px"}>
                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Name <span className={classes.requiedfield}>*</span>
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <TextField
                            fullWidth
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Product Name"
                            size="small"
                            className={classes.textField}
                            InputProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                            InputLabelProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                            error={
                              checkMainItemNameData &&
                              !checkMainItemNameData.status
                            }
                            helperText={
                              checkMainItemNameData &&
                              !checkMainItemNameData.status ? (
                                <span>
                                  {checkMainItemNameData.message} -{" "}
                                  <span
                                    style={{
                                      color: "blue",
                                      cursor: "pointer",
                                      textDecoration: "underline",
                                    }}
                                    onClick={() =>
                                      navigate(
                                        `/main/item/view/${checkMainItemNameData.id}`
                                      )
                                    }
                                  >
                                    {checkMainItemNameData.name}
                                  </span>
                                </span>
                              ) : (
                                ""
                              )
                            }
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item lg={6} md={6} sm={6} xs={6}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">Active</Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <CustomSwitch
                            color="primary"
                            sx={{ mr: 2 }}
                            formik={formik}
                            name="isActive"
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Short Description
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <TextField
                            placeholder="Short Description"
                            size="small"
                            multiline
                            rows={3}
                            {...formik.getFieldProps(
                              "mainItemShortDescription"
                            )}
                            className={classes.textField}
                            InputProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                            InputLabelProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Long Description
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <TextField
                            placeholder="Long Description .."
                            size="small"
                            multiline
                            rows={8}
                            {...formik.getFieldProps("mainItemLongDescription")}
                            className={classes.discriptionTextField}
                            InputProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                            InputLabelProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                <Card className={classes.card}>
                  <Grid container spacing={3} p={"24px"}>
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      sx={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        className={classes.addButton}
                        onClick={cancelFunction}
                      >
                        Reset
                      </Button>
                      <Box m={0.5}></Box>

                      <Button
                        variant="contained"
                        startIcon={
                          isLoading ? (
                            <CircularProgress
                              size={18}
                              className={classes.isLoading}
                            />
                          ) : (
                            <SaveAltIcon />
                          )
                        }
                        className={classes.addButton}
                        onClick={() => formik.handleSubmit()}
                        disabled={!formValid || isLoading}
                      >
                        Save
                      </Button>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
            <Dialog open={openGallery} onClose={closeGalleryDialog}>
            <Grid className={isMobileRes ? classes.DialogMobile :classes.Dialog}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  sx={{ borderBottom: 2, padding: 2 }}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Typography className={classes.cardTitle}>
                      Select an Image from Gallery
                    </Typography>
                  </Grid>
                </Grid>
                <DialogContent>
                  <ImageList
                    sx={{ width: 500, height: 450 }}
                    cols={3}
                    rowHeight={164}
                  >
                    {galleryImages.map((imagePath) => (
                      <ImageListItem key={imagePath}>
                        <img
                          src={`${imagePath}?w=164&h=164&fit=crop&auto=format`}
                          srcSet={`${imagePath}?w=164&h=164&fit=crop&auto=format&dpr=2 2x`}
                          alt="Gallery"
                          loading="lazy"
                          onClick={() => handleGallerySelection(imagePath)}
                          style={{ cursor: "pointer" }}
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </DialogContent>
              </Grid>
            </Dialog>
          </form>
        </Grid>
      </Grid>
    </div>
  );
};

export default Index;
